import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Testimonials() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });

  const testimonials = [
    {
      quote: "This platform transformed how we make decisions. The AI insights have helped us increase revenue by 34% in just six months.",
      author: "Sarah Chen",
      role: "Chief Data Officer",
      company: "TechCorp Global",
      avatar: "https://public.readdy.ai/ai/img_res/ee7b5262b3e9410fbc1b8801134c3417.jpg"
    },
    {
      quote: "The real-time analytics capabilities are unmatched. We can now respond to market changes within minutes instead of days.",
      author: "Michael Rodriguez",
      role: "VP of Analytics",
      company: "RetailMax Inc",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Hispanic%20businessman%20headshot%20portrait%2C%20friendly%20expression%2C%20modern%20office%20setting%2C%20business%20suit%2C%20professional%20lighting%2C%20executive%20photography%20style&width=200&height=200&seq=avatar-002&orientation=squarish"
    },
    {
      quote: "Implementation was seamless and the support team is exceptional. Best analytics investment we've ever made.",
      author: "Emily Thompson",
      role: "Director of Operations",
      company: "FinanceHub",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Caucasian%20businesswoman%20headshot%20portrait%2C%20professional%20demeanor%2C%20contemporary%20office%20background%2C%20business%20attire%2C%20studio%20lighting%2C%20corporate%20photography&width=200&height=200&seq=avatar-003&orientation=squarish"
    },
    {
      quote: "The predictive modeling features have given us a competitive edge. We're now ahead of trends instead of reacting to them.",
      author: "David Park",
      role: "Head of Strategy",
      company: "InnovateLabs",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20headshot%20portrait%2C%20confident%20look%2C%20modern%20workspace%20background%2C%20smart%20casual%20attire%2C%20natural%20professional%20lighting&width=200&height=200&seq=avatar-004&orientation=squarish"
    },
    {
      quote: "Integration with our existing tools was effortless. The unified dashboard has become our single source of truth.",
      author: "Jessica Williams",
      role: "CTO",
      company: "CloudScale Systems",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20African%20American%20businesswoman%20headshot%20portrait%2C%20warm%20smile%2C%20tech%20office%20environment%2C%20professional%20clothing%2C%20soft%20lighting%2C%20executive%20portrait%20style&width=200&height=200&seq=avatar-005&orientation=squarish"
    },
    {
      quote: "ROI was evident within the first quarter. The insights we've gained are invaluable for strategic planning.",
      author: "Robert Kim",
      role: "CFO",
      company: "GrowthVentures",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Asian%20businessman%20headshot%20portrait%2C%20serious%20professional%20expression%2C%20corporate%20office%20background%2C%20formal%20business%20suit%2C%20professional%20studio%20lighting&width=200&height=200&seq=avatar-006&orientation=squarish"
    }
  ];

  return (
    <section className="py-24 px-6 lg:px-16 bg-gradient-to-b from-gray-900 to-gray-950">
      <div className="max-w-7xl mx-auto">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 rounded-full mb-6 border border-yellow-500/30">
            <i className="ri-star-line text-yellow-400"></i>
            <span className="text-sm font-semibold text-yellow-400">TESTIMONIALS</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Trusted by Industry Leaders
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Join thousands of companies using our platform to drive data-driven growth and innovation.
          </p>
        </div>

        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`bg-gray-800/50 p-8 rounded-2xl border border-gray-700 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-600 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-8 leading-relaxed">"{testimonial.quote}"</p>
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.author}
                  className="w-14 h-14 rounded-full object-cover object-top"
                />
                <div>
                  <div className="font-bold text-white">{testimonial.author}</div>
                  <div className="text-sm text-gray-400">{testimonial.role}</div>
                  <div className="text-sm text-teal-400">{testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className={`mt-16 text-center transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-12 flex-wrap">
            {[
              { value: '4.9/5', label: 'Average Rating' },
              { value: '2,500+', label: 'Customer Reviews' },
              { value: '98%', label: 'Satisfaction Rate' },
            ].map((stat, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-500 ${statsVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
